<template>
    <div id="content-template-plano-svg" class="content-template-svg">
        <AddFavorite v-if="favorite !== null"></AddFavorite>
        <MenuLayer :items="itemsLayer" v-show="isActiveLayer" :x="layerX" :y="layerY"></MenuLayer>
        <LoaderInfo v-if="loader"></LoaderInfo>
        <svg id="cluster" class="cluster-builder" width="100%" height="100%"></svg>
    </div>
</template>

<script>
import { utilsMixin } from "../mixins/utils";
import { mapActions, mapGetters } from "vuex";
import { D3Utils } from "../../../utils/D3Utils";
import { MathUtils } from "../../../utils/MathUtils";
import { TYPE_SHELF, TYPE_DIVIDER } from "../../../store/catalog/typeStructure";
import { Shelf } from "../components/Shelf";
import { Caddy } from "../components/Caddy";
import { Group } from "../components/Group";
import { Component } from "../components/Component";
import { Divider } from "../components/Divider";
import { Module } from "../components/Module";
import { Plano } from "../components/Plano";
import { i18n } from "../../../main";
import httpService from "../../service";
import MenuLayer from "./../MenuLayer";
import { HTMLUtils } from "../../../utils/HTMLUtils";
import AddFavorite from "./../dialog/AddFavorite";
import Globals from "./../../globals";
import LoaderInfo from "../../common/LoaderInfo";
import { carcassmodel } from "@/store/planonext/carcassmodel";
import * as plano from "plano";

import * as d3 from "d3";

function findCaddyParent(component) {
    return component.findParentWhere(parent => parent instanceof Caddy);
}

// if module is placed on above/under the destination (under for reversed hooks)
function canSnapModuleVertically(dropPosition, targetComponent, draggedComponentData) {
    if (draggedComponentData.snapUp) {
        return targetComponent.data.y < dropPosition.y && (targetComponent.data.hookInverse || targetComponent.type !== Component.TYPE_SHELF);
    } else if (!targetComponent.data.hookInverse) {
        let dropY = targetComponent.data.y + targetComponent.data.height;

        const caddy = findCaddyParent(targetComponent);
        if (caddy) {
            dropY += caddy.data.y;
        }
        return dropY > dropPosition.y;
    }
    return false;
}

function canSnapModuleHorizontally(globalDraggedComponentBounds, targetComponent, destinationAlreadyOwnsDragged) {
    const draggedRange = new plano.Range({ from: globalDraggedComponentBounds.xStart, to: globalDraggedComponentBounds.xEnd });

    let xStart = targetComponent.data.x;
    const caddy = findCaddyParent(targetComponent);
    if (caddy) {
        xStart += caddy.data.x;
    }
    const xEnd = xStart + targetComponent.data.width;

    const targetRange = new plano.Range({ from: xStart, to: xEnd });
    return draggedRange.intersect(targetRange);
}

const seps = {};
const positionSelection = { x: 0, y: 0 };
const dimSelection = { x: 0, y: 0, width: 0, height: 0 };

function getGlobalBoundsForModuleAt(position, data) {
    Component.parseNumber(data, ["x", "y", "width", "xOrigin", "height"]);
    let xOrigin = data.xOrigin ? data.xOrigin : 0;
    let xStart = position.x - xOrigin;
    let xEnd = xStart + data.width;
    const draggedComponent = Component.allComponents.find(component => component.data.UUID === data.UUID);
    if (draggedComponent) {
        const caddy = findCaddyParent(draggedComponent);
        if (caddy) {
            xStart += caddy.data.x;
            xEnd += caddy.data.x;
        }
    }
    return { xStart, xEnd };
}

export default {
    mixins: [utilsMixin],
    components: {
        MenuLayer,
        AddFavorite,
        LoaderInfo
    },
    props: {
        data: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            loader: false,
            favorite: null,
            gMain: null,
            gMainD3: null,
            svg: null,
            svgD3: null,
            rectWorkspaceD3: null,
            gContentCarcassD3: null,
            gContentComponentD3: null,
            hooks: [],
            clusterSelected: null,
            selectComponent: null,
            positionOrigin: { x: 0, y: 0 },
            separatorsX: null,
            layerX: "0px",
            layerY: "0px",
            itemsLayer: [
                {
                    id: "realistic",
                    action: { func: this.viewRealistic },
                    label: "planobuilder.realistic",
                    active: false
                },
                {
                    id: "highlights",
                    action: { func: this.viewsModule, args: ["highlights"] },
                    label: "planobuilder.highlights",
                    active: true
                },
                {
                    id: "iconscolors",
                    action: { func: this.viewsModule, args: ["iconscolors"] },
                    label: "planobuilder.iconscolors",
                    active: false
                },
                {
                    id: "reference",
                    action: { func: this.viewsModule, args: ["reference"] },
                    label: "planobuilder.reference",
                    active: true
                },
                {
                    id: "modules",
                    action: { func: this.viewsModule, args: ["modules"] },
                    label: "planobuilder.modules",
                    active: true
                }
            ],
            hasSelectorMultiple: false
        };
    },
    watch: {
        onCopyElement(value) {
            this.copyElement(value);
        },

        onCopyFavorite(value) {
            this.copyFavorite(value);
        },

        onCopyScratchpad(value) {
            this.copyFavorite(value, true);
        },

        onSavePlano() {
            this.savePlano();
        },

        onPlanoPositionInit() {
            this.planoPositionInit();
        },

        async onExportPdf() {
            let planoDetails = this.getPlanoDetails;
            this.loader = true;
            let object = {
                level: 1,
                step: 1,
                name: this.data.plano.name,
                pages: [],
                campaign: planoDetails.campaign.name,
                local: i18n.locale
            };
            let svg = this.svg.cloneNode(true);
            svg.style.width = "100%";
            svg.style.height = "100%";

            d3.select(svg)
                .selectAll("svg")
                .select("rect#component-selected")
                .remove();

            let img = await Plano.drawImage(svg);
            img.style.width = "100%";
            img.style.height = "100%";

            let canvas = document.createElement("canvas");
            canvas.width = 1000;
            canvas.height = 1000;
            canvas.style.width = "100%";
            canvas.style.height = "100%";

            let ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            let image = new Image();

            let png = canvas.toDataURL("image/png");
            image.src = png;

            let page = {
                content: image.outerHTML,
                pager: "1/1",
                title: this.data.plano.name,
                campaign: planoDetails.campaign.name
            };

            object.pages.push(page);

            httpService.post("/generate-svg-pdf", JSON.stringify(object)).then(response => {
                this.loader = false;
                let url = Globals.getBaseUrl() + response.url;
                window.open(url);
            });
        },

        onZoomComponent(value) {
            let { active } = value;
            this.planoPositionInit();
            if (active) {
                let components = Component.getSelectedComponents();
                if (components.length > 0) {
                    let component = components[0];
                    let { x, y, width, height } = component.getData();
                    this.svgD3.attr("viewBox", x + " " + y + " " + width + " " + height);
                }
            }
        },

        getPosMenuLayer(pos) {
            let parent = document.querySelector("div.content-template-svg");
            let dim = parent.getBoundingClientRect();
            let x = pos.x - dim.x + pos.width + 2;
            let y = pos.y - dim.y;
            this.layerX = x + "px";
            this.layerY = y + "px";
        },

        isPan(value) {
            if (value) {
                this.enablePan();
            } else {
                this.disablePan();
            }
        }
    },
    computed: {
        ...mapGetters("planonext/carcass/", {
            getWorkspace: "getWorkspace",
            getOuter: "getOuter",
            getComponentSelected: "getComponentSelected",
            getInner: "getInner",
            getHeader: "getHeader",
            getFooter: "getFooter",
            getDividersSlot: "getDividers",
            getComponentById: "getComponentById",
            istPitchXConstant: "istPitchXConstant",
            istPitchYConstant: "istPitchYConstant",
            getPitchsX: "getPitchsX",
            getPitchsY: "getPitchsY",
            getChildren: "getChildren",
            getBaseBoxes: "getBaseBoxes",
            getClusters: "getClusters"
        }),

        ...mapGetters("planonext/plano", {
            getDividers: "getDividers",
            getGroups: "getGroups",
            getGroupByModule: "getGroupByModule"
        }),

        ...mapGetters("events/planomassmarket", {
            onCopyElement: "onCopyElement",
            onSavePlano: "onSavePlano",
            onPlanoPositionInit: "onPlanoPositionInit",
            onZoomComponent: "onZoomComponent",
            onCopyFavorite: "onCopyFavorite",
            onCopyScratchpad: "onCopyScratchpad",
            onExportPdf: "onExportPdf"
        }),

        ...mapGetters("plano/status", {
            getMode: "getMode",
            isActiveLayer: "isActiveLayer",
            getPosMenuLayer: "getPosMenuLayer",
            isPan: "isPan",
            isFreePosition: "isFreePosition"
        }),

        ...mapGetters("plano/data", {
            getPlanoDetails: "getPlanoDetails",
            isCaddySystem: "isCaddySystem"
        }),

        ...mapGetters("gui/menubar", {
            isActive: "isActive"
        }),

        planoId() {
            return this.$route.params.id;
        }
    },
    created() {},
    async mounted() {
        this.loadCarcass(this.getCarcass());
        this.drawCarcass();
        this.initListeners();
        await this.loadComponents();
    },

    beforeDestroy() {
        this.setMode("plano");
        this.cleanState();
        this.initViews();
        Component.cleanComponents();
    },

    methods: {
        ...mapActions("planonext/carcass/", {
            cleanState: "cleanState",
            loadCarcass: "loadCarcass"
        }),

        ...mapActions("plano/status", {
            setMode: "setMode",
            setModulesEdit: "setModulesEdit",
            addViewActive: "addViewActive",
            removeViewActive: "removeViewActive",
            initViews: "initViews",
            setPan: "setPan"
        }),

        ...mapActions("plano/data", {
            listFavorites: "listFavorites"
        }),

        ...mapActions("gui/menubar", {
            addMenuItemActive: "addMenuItemActive"
        }),

        savePlano(displayDialog = true) {
            return httpService.post("/plano/" + this.$route.params.id + "/config", JSON.stringify(this.getJsonSave())).then(res => {
                if (displayDialog) this.dialogSuccess("Save");
            });
        },

        planoPositionInit() {
            this.gMainD3.attr("transform", d => {
                if (d.transform === null) return null;
                d.transform.k = 1;
                d.transform.x = 0;
                d.transform.y = 0;
                return d.transform;
            });
            let workspace = this.getWorkspace;
            this.svgD3.attr("viewBox", "-100 -100 " + (workspace.width + 100) + " " + (workspace.height + 100));
        },

        getJsonSave() {
            let groups = this.isCaddySystem ? Component.getJsonCaddySystem() : Component.getJsonGroups();
            let dividers = Component.getJsonDividers();
            let objectToSave = {
                content: {
                    plano: this.data.plano ? this.data.plano : this.data,
                    config: {
                        groups: groups,
                        dividers: dividers
                    }
                },
                isLocked: true
            };
            return objectToSave;
        },

        test() {
            console.log(this.getSeparators(), "======= separators ========");
            console.log(Component.getAllComponents(), "allll component ===");
            console.log(this.getMode, "==== mode ======");
        },

        contextMenu() {
            d3.event.preventDefault();
            let ctrl = "Ctrl";
            let items = [
                {
                    shortcuts: { keyboard: ctrl + " + e" },
                    label: { fr: "Editer", en: "Edit" },
                    action: this.editComponent
                },
                {
                    shortcuts: { keyboard: ctrl + " + d" },
                    label: { fr: "Dupliquer", en: "Duplicate" },
                    action: this.duplicate
                },
                {
                    shortcuts: { keyboard: ctrl + " + f" },
                    label: {
                        fr: "Ajouter aux favoris",
                        en: "Add to favorites"
                    },
                    action: this.addFavorite
                },
                {
                    shortcuts: { keyboard: ctrl + " + p" },
                    label: {
                        fr: "Placer dans le scratchpad",
                        en: "Place in the scratchpad"
                    },
                    action: this.addScratchpad
                },
                {
                    shortcuts: { keyboard: "del" },
                    label: { fr: "Supprimer", en: "Delete" },
                    action: this.deleteModuleAction
                },
                {
                    label: { fr: "Composant visuel", en: "Visual componant" },
                    action: this.openMenuVisualComponent,
                    event: "mouseover"
                }
            ];
            if (Component.getSelectedComponents().length > 0) this.createMenu(items);
        },

        openMenuVisualComponent() {
            let items = [
                {
                    label: { fr: "Bleu", en: "Blue" },
                    action: this.newVisualComponent,
                    args: ["blue", "#41B4E8"]
                },
                {
                    label: { fr: "Rouge", en: "Red" },
                    action: this.newVisualComponent,
                    args: ["red", "#FF0000"]
                },
                {
                    label: { fr: "Violet", en: "Purple" },
                    action: this.newVisualComponent,
                    args: ["purple", "#8A2BE2"]
                },
                {
                    label: { fr: "Jaune", en: "Yellow" },
                    action: this.newVisualComponent,
                    args: ["yellow", "#FFD700"]
                },
                {
                    label: { fr: "Vert", en: "Green" },
                    action: this.newVisualComponent,
                    args: ["green", "#008000"]
                },
                {
                    label: { fr: "Annuler", en: "Cancel" },
                    action: this.newVisualComponent,
                    args: ["cancel"]
                }
            ];

            this.createMenu(items, true);
        },

        addScratchpad() {
            let components = Component.getSelectedComponents();
            if (components.length === 1) {
                let component = components[0];
                let obj = Component.createSvgFavorite(component);
                let scratchpad = {
                    type: "WALL",
                    component: component.getJson(),
                    svg: obj.elt,
                    size: obj.size
                };

                let svgStr = scratchpad.svg.outerHTML.replace(new RegExp("&nbsp;", "g"), ",");

                let scrachSave = {
                    type: scratchpad.type,
                    component: this.assign(scratchpad.component),
                    size: scratchpad.size,
                    hookInverse: obj.hookInverse
                };
                let objSave = {
                    svg: svgStr,
                    name: this.name,
                    content: scrachSave,
                    planoId: this.$route.params.id
                };

                httpService.post("/favorites", JSON.stringify(objSave)).then(_ => {
                    component.delete();
                    this.dialogSuccess("Save Scratchpad");
                    this.savePlano(false);
                    let idMenu = "menu_plano_massmarket";
                    let id = "menuitem_plano_massmarket_scratchpad";
                    if (!this.isActive(idMenu, id)) {
                        this.addMenuItemActive({ idMenu, id });
                    }
                    this.listFavorites(this.planoId);
                });
            }
        },

        addFavorite() {
            let components = Component.getSelectedComponents();
            if (components.length === 1) {
                let component = components[0];
                let obj = Component.createSvgFavorite(component);
                let favorite = {
                    type: "WALL",
                    component: component.getJson(),
                    svg: obj.elt,
                    size: obj.size,
                    hookInverse: obj.hookInverse
                };

                this.favorite = favorite;
            }
        },

        newVisualComponent(type) {
            let types = {
                blue: "#41B4E8",
                red: "#FF0000",
                purple: "#8A2BE2",
                yellow: "#FFD700",
                green: "#008000",
                cancel: "cancel"
            };

            let color = types[type];
            Component.getSelectedComponents().forEach(cmp => {
                if (cmp.type === Component.TYPE_MODULE) cmp.visualComponent(color);
            });
        },

        deleteModuleAction() {
            Component.getSelectedComponents().forEach(cmp => {
                if (cmp.type === Component.TYPE_MODULE) {
                    let parent = cmp.getParent();
                    if (parent.type === Component.TYPE_GROUP && parent.getComponents().length === 1) {
                        parent.delete();
                    } else {
                        cmp.delete();
                    }
                } else {
                    cmp.delete();
                }
            });
        },

        editComponent() {
            let cmps = Component.getSelectedComponents();
            let modules = cmps.map(cmp => cmp.getData());
            if (cmps.length === 1) {
                let cmp = cmps[0];
                if (cmp.type === Component.TYPE_MODULE) {
                    this.setModulesEdit(modules);
                    this.setMode("module");
                } else if (cmp.type === Component.TYPE_SHELF) {
                    this.setMode(cmp.data.isCaddy ? "caddy" : "shelf");
                }
            }
        },

        async duplicate() {
            let cmpSelected = Component.getSelectedComponents()[0];
            if (cmpSelected.type === Component.TYPE_SHELF) {
                let UUIDGroup = "group_" + this.UUID();
                let groupData = {
                    UUID: UUIDGroup,
                    x: 0,
                    y: 0
                };
                let group = Group.createRoot(groupData, this.gContentComponentD3);
                let cmp = await cmpSelected.duplicate(group);
                this.duplicateChildren(cmpSelected, cmp);
                this.dragShelf(cmp);
            } else {
                let cmp = await cmpSelected.duplicate();
                this.duplicateChildren(cmpSelected, cmp);
                this.dragModule(cmp);
            }
        },

        async duplicateChildren(component, parent) {
            component.getComponents().forEach(child => {
                child.duplicate(parent).then(res => {
                    this.dragModule(res);
                    this.duplicateChildren(child, res);
                });
            });
        },

        viewsModule(view, active) {
            let make = true;
            let modules = Component.getAllModules();
            if (view === "modules") {
                modules.forEach(module => module.setVisible(active));
                make = !active;
            }

            if (make) {
                if (view === "iconscolors") {
                    if (active) {
                        this.addViewActive("color");
                    } else {
                        this.removeViewActive("color");
                    }
                }
                if (view === "reference") {
                    if (active) {
                        this.addViewActive("ref");
                    } else {
                        this.removeViewActive("ref");
                    }
                }

                if (view === "highlights") {
                    if (active) {
                        this.addViewActive("highlights");
                    } else {
                        this.removeViewActive("highlights");
                    }
                }

                modules.forEach(module => {
                    module.loadColors();
                    module.loadHighLight();
                });
            }
        },

        viewRealistic(active) {
            let modules = Component.getAllModules();
            if (active) {
                this.addViewActive("realistic");
            } else {
                this.removeViewActive("realistic");
            }
            modules.forEach(module => module.viewRealistic(active));
        },

        createMenu(items, menuItem = false, menuHide = false, event = null) {
            event = event === null ? d3.event : event;
            let x = event.clientX;
            let y = event.clientY;
            d3.select("div.content-menu-item").remove();
            if (menuItem === false) {
                d3.select("div.content-menu").remove();
            }

            let contentMenu = document.createElement("div");
            let className = menuItem ? "content-menu content-menu-item" : "content-menu";
            if (menuHide) {
                className = "content-menu-hide";
            }

            contentMenu.className = className;
            contentMenu.style.left = x + "px";
            contentMenu.style.top = y + "px";
            let addclass = "";
            items.forEach(item => {
                let itemElt = document.createElement("div");
                if (menuHide) {
                    let iELt = document.createElement("span");
                    iELt.classList.add("layer-icon", "layer-icon-hidden");
                    iELt.id = "layer-icon-" + item.id;
                    itemElt.appendChild(iELt);
                }
                if (item.addClass) {
                    addclass = " " + item.addClass;
                }
                itemElt.className = (menuItem ? "parent-item-menu menu-item" : "menu-item") + addclass;
                let label = "";
                if (item.label instanceof Object) {
                    label = item.label[i18n.locale];
                } else {
                    label = this.$t(item.label);
                }

                itemElt.appendChild(document.createTextNode(label));
                contentMenu.appendChild(itemElt);

                let { shortcuts } = item;
                if (shortcuts) {
                    let span = document.createElement("span");
                    span.style.display = "inline-block";
                    span.style.marginLeft = "4px";
                    span.style.textDecoration = "underline";
                    span.appendChild(document.createTextNode(shortcuts.keyboard));
                    itemElt.appendChild(span);
                }

                let args = null;
                let event = item.event === undefined ? "click" : item.event;
                d3.select(itemElt).on("mouseover", (d, i, cmps) => {
                    if (!d3.event.target.className.includes("parent-item-menu")) {
                        d3.select("div.content-menu-item").remove();
                    }
                });
                d3.select(itemElt).on(event, () => {
                    args = item.args === undefined ? null : item.args;
                    if (args !== null) item.action.apply(this, args);
                    else item.action.apply(this, args);
                });
            });

            document.body.appendChild(contentMenu);

            d3.select("body").on("click", () => {
                if (menuHide === true) {
                    if (this.tabhide === false) d3.selectAll("div.content-menu-hide").remove();
                } else {
                    d3.selectAll("div.content-menu").remove();
                }
            });
        },

        getSizePx() {
            let rect = this.svgD3.select("rect#rect-size-unit");
            let bBox = rect.node().getBoundingClientRect();
            return { width: bBox.width, height: bBox.height };
        },

        getMousePos(evt, svg) {
            let CTM = svg.getScreenCTM();
            let pnt = svg.createSVGPoint();
            pnt.x = evt.clientX;
            pnt.y = evt.clientY;
            let transform = this.getTransformPan();
            let iPNT = pnt.matrixTransform(CTM.inverse());
            let x = iPNT.x;
            let y = iPNT.y;
            if (transform != null) {
                x = (x - transform.x) / transform.k;
                y = (y - transform.y) / transform.k;
            }
            x = Math.floor(x);
            y = Math.floor(y);
            return { x, y };
        },

        dragMousedown(eltD3, start, dragged, end) {
            D3Utils.drag(eltD3, start, dragged, end);
            let init = {
                bubbles: true,
                cancelable: true,
                view: window
            };
            let evt = new MouseEvent("mousedown", init);
            eltD3.node().dispatchEvent(evt);
        },

        getTransformPan() {
            return this.gMainD3.data()[0].transform;
        },

        enablePan() {
            let gPan = this.svgD3.select("g#content-pan");
            this.setPan(true);
            if (gPan.size() === 0) {
                gPan = this.svgD3
                    .append("g")
                    .attr("id", "content-pan")
                    .raise();

                gPan.append("rect")
                    .attr("fill-opacity", 0)
                    .attr("width", 40000)
                    .attr("height", 40000)
                    .attr("x", -20000)
                    .attr("y", -20000);

                gPan.call(this.zoom());
            }

            gPan.attr("visibility", "visible");
        },

        disablePan() {
            this.setPan(false);
            let gPan = this.svgD3.select("g#content-pan");
            gPan.attr("visibility", "hidden");
        },

        zoom() {
            let gMain = this.gMainD3;
            let d = gMain.data()[0];
            this.zoomEvent = d3
                .zoom()
                .scaleExtent([1, 20])
                .on("zoom", () => {
                    let transform = d3.event.transform;
                    d.transform = transform;
                    gMain.attr("transform", transform);
                });
            // }
            return this.zoomEvent;
        },

        getSizeComponent(size) {
            let viewBox = this.svgD3.attr("viewBox");
            let sizeViewBox = viewBox.split(" ");
            let widthViewBoxSvg = parseInt(sizeViewBox[2], 10);
            let heightViewBoxSvg = parseInt(sizeViewBox[3], 10);
            let elt = document.querySelector("g#content-plano");
            let bb = elt.getBoundingClientRect();
            let width = size.width * (bb.width / widthViewBoxSvg);
            let height = size.height * (bb.height / heightViewBoxSvg);
            return { width: width, height: height };
        },

        copyFavorite(obj, scratchpad = false) {
            let parent = document.querySelector("div#content-template-plano-svg");
            let info = parent.getBoundingClientRect();
            let { data, elt } = obj;
            let eltCopy = elt.cloneNode(true);
            let size = this.getSizeComponent(data.size);
            let { width, height } = size;
            let top = obj.data.hookInverse ? event.y - info.y : event.y - info.y - height;
            eltCopy.style.position = "absolute";
            eltCopy.style.zIndex = 100;
            eltCopy.style.height = height + "px";
            eltCopy.style.width = width + "px";
            eltCopy.style.left = event.x - info.x + "px";
            eltCopy.style.top = top + "px";

            parent.appendChild(eltCopy);

            let dataCop = this.assign(data);
            let eltD3 = d3.select(eltCopy).data([dataCop]);

            this.dragMousedown(
                eltD3,
                null,
                (d, i, cmps) => {
                    let event = d3.event;
                    let element = cmps[i];
                    let x = event.sourceEvent.x - info.x;
                    let y = obj.data.hookInverse ? event.sourceEvent.y - info.y : event.sourceEvent.y - info.y - height;
                    element.style.left = x + "px";
                    element.style.top = y + "px";
                },
                async (d, i, cmps) => {
                    let event = d3.event;
                    let element = cmps[i];
                    let { type, data } = d.component;

                    let cmp = null;

                    if (type === Component.TYPE_MODULE) {
                        let pos = this.getMousePos(event.sourceEvent, this.svg);
                        cmp = await this.makeSnapModule(data, pos);
                    } else if (type === Component.TYPE_SHELF) {
                        let { clientY, clientX } = event.sourceEvent;
                        let posEvent = { clientX, clientY };
                        let pos = this.getMousePos(posEvent, this.svg);
                        cmp = await this.makeSnapShelf(data, pos);
                    }
                    if (cmp !== null) {
                        await this.createChildFav(cmp, d.component.components);
                        if (scratchpad === true) this.deleteScratchpad(obj);
                    }
                    element.parentNode.removeChild(element);
                }
            );
        },

        deleteScratchpad(obj) {
            httpService.delete("/favorites/scratchPad/" + obj.id).then(_ => {
                this.savePlano(false);
                this.listFavorites(this.planoId);
            });
        },

        async createChildFav(parent, childrenData) {
            if (parent) {
                let { data } = parent;
                let { x, y, posModY } = data;
                let hookInverse = !!data.hookInverse;
                let heightChild;
                let cmp = null;
                let snapUp;
                let yPos;
                childrenData.forEach(async dataChidl => {
                    snapUp = !!dataChidl.data.snapUp;
                    posModY = !posModY ? 0 : posModY;
                    let xPos = x + dataChidl.data.xParent;
                    heightChild = dataChidl.data.height;
                    if (hookInverse && snapUp) {
                        yPos = y + posModY;
                    } else {
                        yPos = y - heightChild + posModY;
                    }
                    dataChidl.data.x = xPos;
                    dataChidl.data.y = yPos;
                    cmp = await this.createModule(dataChidl.data, parent);
                    if (dataChidl.components.length > 0) await this.createChildFav(cmp, dataChidl.components);
                });

                return cmp;
            }
        },

        getImgPathSvg(imgPath) {
            let ret = this._.find(imgPath.figures_advanced, o => o.id_type_file === 6);

            if (typeof ret !== "undefined") {
                return ret.big;
            } else {
                return "/static/assets/svg/generic_module-01.svg";
            }
        },

        getPlanoImgReal(imgPath) {
            let ret = this._.find(imgPath.figures_advanced, o => o.id_type_file === 5);

            if (typeof ret !== "undefined") {
                return ret.big;
            } else {
                return null;
            }
        },

        /**
         * On menu component drag, we clone the html node
         * and listen to drag events. To then trigger drop actions on release
         */
        copyElement(obj) {
            let parent = document.querySelector("div#content-template-plano-svg");
            let info = parent.getBoundingClientRect();
            let sizeUnit = this.getSizePx();
            let { elt, data, event } = obj;
            let conf = data.config;
            let dataComponent = this.assign(conf);

            dataComponent.id = data.id;
            dataComponent.src = this.getImgPathSvg(data);
            dataComponent.srcreal = this.getPlanoImgReal(data);
            dataComponent.config = conf;

            delete dataComponent.groups;
            delete dataComponent.tickets;
            delete dataComponent.products;

            let height = sizeUnit.height * conf.height;
            let width = sizeUnit.width * conf.width;
            let k = this.getTransformPan() === null ? 1 : this.getTransformPan().k;
            let eltCopy = elt.cloneNode(true);
            eltCopy.removeAttribute("class");
            eltCopy.style.position = "absolute";
            eltCopy.style.zIndex = 100;
            eltCopy.style.height = height * k + "px";
            eltCopy.style.width = width * k + "px";
            eltCopy.style.left = event.x - info.x + "px";
            eltCopy.style.top = event.y - info.y + "px";

            parent.appendChild(eltCopy);

            let eltD3 = d3.select(eltCopy).data([dataComponent]);

            const onDragUpate = (d, i, cmps) => {
                let event = d3.event;
                let element = cmps[i];
                let x = event.sourceEvent.x - info.x;
                let y = event.sourceEvent.y - info.y;
                element.style.left = x + "px";
                element.style.top = y + "px";
            };
            const onDragEnd = (componentData, i, cmps) => {
                let event = d3.event;
                let element = cmps[i];
                let pos = this.getMousePos(event.sourceEvent, this.svg);

                // componentData is only a json object, not an actual Component.
                // so we can't use instanceof here
                switch (componentData.type) {
                    case TYPE_SHELF: {
                        this.makeSnapShelf(componentData, pos);
                        break;
                    }
                    case TYPE_DIVIDER: {
                        this.makeSnapDivider(componentData, pos);
                        break;
                    }
                    default: {
                        this.makeSnapModule(componentData, pos);
                    }
                }
                element.parentNode.removeChild(element);
            };

            this.dragMousedown(eltD3, undefined, onDragUpate, onDragEnd);
        },

        generateKey(xStart, xEnd, width, y) {
            let key = xStart + "_" + xEnd + "_" + width + "_" + y;
            return key;
        },

        getCarcass() {
            return this.data.plano ? this.data.plano : this.data;
        },

        getSeparators() {
            return this.separatorsX;
        },

        drawCarcass() {
            this.init();
            this.drawOuter();
            this.drawInner();
            this.drawBaseBoxes();
            this.drawDividers();
            this.drawHooks();
            this.drawHeader();
            this.drawFooter();
            this.initSeparators();
            let rect = this.drawStructure(this.getOuter, this.gContentComponentD3);
            rect.attr("fill-opacity", 0);
        },

        init() {
            let svg = document.querySelector("svg#cluster");
            let svgD3 = d3.select(svg);
            this.svg = svg;
            this.svgD3 = svgD3;

            svgD3
                .append("rect")
                .data([{ width: 1, height: 1 }])
                .attr("id", "rect-size-unit")
                .attr("class", "rect-size")
                .attr("width", 1)
                .attr("height", 1)
                .attr("opacity", 0);

            let workspace = this.getWorkspace;

            svgD3.attr("viewBox", "-100 -100 " + (workspace.width + 100) + " " + (workspace.height + 100));
            this.gMainD3 = svgD3.append("g").data([{ transform: null }]);

            this.gMain = this.gMainD3.node();

            this.rectWorkspaceD3 = this.gMainD3
                .append("rect")
                .attr("id", workspace.id)
                .attr("fill", "#FFFFFF")
                .attr("stroke", "black")
                .attr("width", workspace.width)
                .attr("height", workspace.height);

            this.gContentCarcassD3 = this.gMainD3
                .append("g")
                .raise()
                .attr("id", "content-carcass");

            this.gContentComponentD3 = this.gMainD3
                .append("g")
                .raise()
                .attr("id", "content-plano");

            Plano.setSvgMain(this.svg);
            Plano.setGMainD3(this.gMainD3);
        },

        initSeparators() {
            let hooks = [];
            let separatorsX = Object.values(seps);
            if (separatorsX.length === 0) {
                this.hooks.forEach(h => {
                    hooks.push({ x: h.x, y: h.y });
                });

                hooks.sort((h1, h2) => h1.x - h2.x);
                let hks;
                let obj;
                let xStart, xEnd, width, y, key;
                hooks.forEach((hook, index, array) => {
                    hks = array.filter(h => h.x > hook.x);
                    hks.forEach(hk => {
                        xStart = hook.x;
                        xEnd = hk.x;
                        width = hk.x - hook.x;
                        y = hook.y;
                        key = this.generateKey(xStart, xEnd, width, y);
                        if (!seps[key]) {
                            obj = {
                                xStart: hook.x,
                                xEnd: hk.x,
                                width: hk.x - hook.x,
                                y: hook.y
                            };
                            seps[key] = obj;
                            separatorsX.push(obj);
                        }
                        // }
                    });
                });
            }
            this.separatorsX = separatorsX;
        },

        drawStructure(structure, parentD3 = null) {
            parentD3 = parentD3 === null ? this.gContentCarcassD3 : parentD3;
            return parentD3
                .append("rect")
                .raise()
                .attr("id", structure.id)
                .attr("stroke", "black")
                .attr("fill", "#fff")
                .attr("width", structure.width)
                .attr("height", structure.height)
                .attr("x", structure.x)
                .attr("y", structure.y);
        },

        drawOuter() {
            let outer = this.getOuter;
            if (outer) {
                this.drawStructure(outer);
            }
        },

        drawInner() {
            let inner = this.getInner;
            if (inner) {
                this.drawStructure(inner);
            }
        },

        drawHooks() {
            let pitchsX = this.getPitchsX;
            let pitchsY = this.getPitchsY;
            let resX, resY;
            let x, y;
            let inner = this.getInner;
            let pitchsXConstant = [];
            let columns = [];
            let rows = [];
            if (pitchsX.length > 0) {
                if (this.istPitchXConstant) {
                    let pitchXConstant = pitchsX[0];
                    resX = pitchXConstant.xOrigin + inner.x;
                    pitchsXConstant.push(resX);
                    x = pitchXConstant.x;
                    for (let i = 0, l = pitchXConstant.repeat; i < l; i++) {
                        resX += x;
                        pitchsXConstant.push(resX);
                    }
                    resX = inner.x + inner.width - pitchXConstant.xEnd;

                    pitchsXConstant.push(resX);
                } else {
                    resX = inner.x;
                    pitchsX.forEach(pitch => {
                        x = resX + pitch.x;
                        pitchsXConstant.push(x);
                    });
                }
            }
            let pitchsYConstant = [];
            if (pitchsY.length > 0) {
                if (this.istPitchYConstant) {
                    let pitchYConstant = pitchsY[0];
                    resY = pitchYConstant.yOrigin + inner.y;
                    pitchsYConstant.push(resY);
                    y = pitchYConstant.y;
                    for (let i = 0, l = pitchYConstant.repeat; i < l; i++) {
                        resY += y;
                        pitchsYConstant.push(resY);
                    }
                    resY = inner.y + inner.height - pitchYConstant.yEnd;
                    pitchsYConstant.push(resY);
                } else {
                    resY = inner.y;
                    pitchsY.forEach(pitch => {
                        y = resY + pitch.y;
                        pitchsYConstant.push(y);
                    });
                }

                pitchsXConstant.forEach((pX, i) => {
                    pitchsYConstant.forEach(pY => {
                        this.hooks.push({ x: pX, y: pY });
                        this.drawHook(pX, pY);
                    });
                });

                let len = pitchsYConstant.length;
                pitchsYConstant.forEach((pY, i) => {
                    rows.push({ y: pY, name: len - i });
                });
                if (this.getPlanoDetails.template.systemType.id === carcassmodel.PLANO_CATEGORY_SYSTEM) {
                    let alp = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
                    let name = "";
                    let repeat = 1;
                    pitchsXConstant.forEach((pX, i) => {
                        name = alp.charAt(i % 26);
                        repeat = Math.ceil(i / 26);
                        repeat = repeat === 0 ? 1 : repeat;
                        name = name.repeat(repeat);
                        columns.push({ x: pX, name: name });
                    });

                    for (const row of rows) {
                        this.gContentComponentD3
                            .append("text")
                            .style("font-size", 50)
                            .attr("x", this.getInner.x - 50)
                            .attr("y", row.y - 20)
                            .text(row.name);
                    }

                    for (const column of columns) {
                        this.gContentComponentD3
                            .append("text")
                            .raise()
                            .style("font-size", 50)
                            .attr("x", column.x + 20)
                            .attr("y", this.getOuter.y - 10)
                            .text(column.name);
                    }
                }
            }
        },

        drawHook(x, y) {
            let data = { x, y };
            this.gContentCarcassD3
                .append("circle")
                .raise()
                .data([data])
                .attr("class", "hook-for-component")
                .attr("cx", x)
                .attr("cy", y)
                .attr("r", 2)
                .attr("fill", "#000");
        },

        drawBaseBoxes() {
            let baseBoxes = this.getBaseBoxes;
            baseBoxes.forEach(baseBox => {
                this.drawStructure(baseBox);
            });
        },

        drawDividers() {
            let dividers = this.getDividersSlot;
            dividers.forEach(divider => {
                this.drawStructure(divider);
            });
        },

        drawHeader() {
            let header = this.getHeader;

            if (header) {
                this.drawStructure(header).attr("opacity", 0.6);
            }
        },

        drawFooter() {
            let footer = this.getFooter;

            if (footer) {
                this.drawStructure(footer).attr("opacity", 0.6);
            }
        },

        initListeners() {
            const onMultiKeyPress = (/** @type{Node} */ node, cb) => {
                let map = {};
                node.addEventListener("keydown", e => {
                    map[e.keyCode] = e;
                    cb(e, map);
                });
                node.addEventListener("keyup", e => {
                    delete map[e.keyCode];
                    if (e.keyCode === 91) {
                        map = {};
                    }
                });
            };

            document.body.addEventListener(
                "wheel",
                e => {
                    if (e.ctrlKey) {
                        e.stopPropagation();
                    }
                },
                { passive: true }
            );

            onMultiKeyPress(document.body, (e, keysCode) => {
                const del = "46";
                if (e.keyCode === 18) {
                    e.stopPropagation();
                    e.preventDefault();
                    this.enablePan();
                }

                let keys = Object.keys(keysCode);

                if (keys.length === 1 && keys.includes(del)) {
                    this.deleteModuleAction();
                }

                if (keys.includes("16")) {
                    this.enableSelectorMultiple();
                }

                if (keys.includes("17") || keys.includes("91")) {
                    e.stopPropagation();
                    e.preventDefault();

                    // Edit ctr + e
                    if (keys.includes("69")) {
                        this.editComponent();
                    }

                    // Favorite ctr + f
                    if (keys.includes("70")) {
                        this.addFavorite();
                    }

                    // scratchpad ctr + p
                    if (keys.includes("80")) {
                        this.addScratchpad();
                    }

                    // Duplicate ctr + d
                    if (keys.includes("68")) {
                        this.duplicate();
                    }

                    // Save ctr + s
                    if (keys.includes("83")) {
                        this.savePlano();
                    }
                }
            });

            document.body.addEventListener("keyup", e => {
                if (e.keyCode === 18) {
                    e.stopPropagation();
                    e.preventDefault();
                    this.disablePan();
                }

                if (e.keyCode === 84) {
                    e.stopPropagation();
                    e.preventDefault();
                }

                if (!e.shiftKey) {
                    this.svgD3.select("g#content-selection-multiple").attr("visibility", "hidden");
                    this.gContentComponentD3.select("rect#rect-selection-multiple").remove();
                }
            });
            this.svg.addEventListener("mousedown", e => {
                e.preventDefault();
                e.stopPropagation();
                if (e.shiftKey) {
                    Component.deselectedComponents();
                    this.hasSelectorMultiple = true;
                }
            });

            this.svg.addEventListener("mousemove", e => {
                e.preventDefault();
                e.stopPropagation();
                if (e.shiftKey) {
                    this.createSelectorMultiple(e);
                }
            });

            this.svg.addEventListener("mouseup", e => {
                e.preventDefault();
                e.stopPropagation();
                this.disableSelectorMultiple(e);
                if (e.button === 0 && !e.shiftKey) Component.deselectedComponents();
            });

            d3.select("svg#cluster").on("contextmenu", this.contextMenu);
        },

        enableSelectorMultiple: function() {
            let gSelection = this.svgD3.select("g#content-selection-multiple");
            if (gSelection.size() === 0) {
                gSelection = this.svgD3
                    .append("g")
                    .attr("id", "content-selection-multiple")
                    .raise();

                gSelection
                    .append("rect")
                    .attr("fill-opacity", 0)
                    .attr("width", 40000)
                    .attr("height", 40000)
                    .attr("x", -20000)
                    .attr("y", -20000);
            }

            gSelection.attr("visibility", "visible");
        },

        createSelectorMultiple(e) {
            if (this.hasSelectorMultiple) {
                let pos = this.getMousePos(e, this.svg);
                let rect = this.gContentComponentD3.select("rect#rect-selection-multiple");
                let { x, y } = pos;
                if (rect.size() === 0) {
                    positionSelection.x = x;
                    positionSelection.y = y;
                    rect = this.gContentComponentD3
                        .append("rect")
                        .attr("id", "rect-selection-multiple")
                        .attr("stroke", "#41B4E8")
                        .attr("stroke-width", 5)
                        .attr("fill-opacity", 0.1)
                        .attr("fill", "#41B4E8");
                }
                let widthTmp = x - positionSelection.x;
                let heightTmp = y - positionSelection.y;
                let width = widthTmp;
                let height = heightTmp;
                if (widthTmp < 0) {
                    width = Math.abs(widthTmp);
                } else {
                    x = positionSelection.x;
                }

                if (heightTmp < 0) {
                    height = Math.abs(heightTmp);
                } else {
                    y = positionSelection.y;
                }
                rect.attr("width", width)
                    .attr("height", height)
                    .attr("x", x)
                    .attr("y", y);

                Object.assign(dimSelection, { x, y, width, height });
            }
        },

        disableSelectorMultiple(e) {
            if (e.shiftKey) {
                let cmps = Component.getAllComponents().filter(cmp => cmp.type !== Component.TYPE_GROUP);
                if (Object.values(dimSelection).filter(value => value > 0).length === 0) return;
                let { x, y, width, height } = dimSelection;
                let xCmp, yCmp, widthCmp, heightCmp;
                let cmpsSelect = cmps.filter(cmp => {
                    xCmp = cmp.data.x;
                    yCmp = cmp.data.y;
                    widthCmp = cmp.data.width;
                    heightCmp = cmp.data.height;
                    return (
                        (xCmp >= x || xCmp + widthCmp >= x) &&
                        (xCmp <= x + width || xCmp + widthCmp <= x + width) &&
                        ((yCmp >= y || yCmp + heightCmp >= y) && (yCmp <= y + height || yCmp + heightCmp <= y + height))
                    );
                });
                cmpsSelect.forEach(cmp => {
                    cmp.selectedComponent(true);
                });
                this.hasSelectorMultiple = false;
                this.gContentComponentD3.select("rect#rect-selection-multiple").remove();
                Object.assign(dimSelection, {
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0
                });
            }
        },

        async loadComponents() {
            let data = this.data;
            let { config } = data;
            if (config) {
                let { groups } = config;
                if (groups) {
                    groups.forEach(group => {
                        this.loadComponent(group);
                    });
                }

                let { dividers } = config;

                if (dividers) {
                    dividers.forEach(divider => {
                        this.createComponent(divider);
                    });
                }
            }
        },

        async loadComponent(data, parent = null) {
            let cmp = await this.createComponent(data, parent);
            let { components } = data;
            if (components) {
                components.forEach(component => {
                    this.loadComponent(component, cmp);
                });
            }
        },

        async createComponent(data, parent = null) {
            let cmp;
            if (data.type === Component.TYPE_GROUP) {
                cmp = !parent ? this.createRootGroup(this.gContentComponentD3, data.data) : this.createGroup(parent, data.data);
            } else if (data.type === Component.TYPE_SHELF) {
                cmp = await this.createShelf(data.data, parent);
            } else if (data.type === Component.TYPE_MODULE) {
                cmp = await this.createModule(data.data, parent);
            } else if (data.type === Component.TYPE_DIVIDER) {
                cmp = await this.createDivider(data.data);
            }

            return cmp;
        },

        createRootGroup(parent, data = undefined) {
            return Group.createRoot(
                data || {
                    UUID: `group_${this.UUID()}`,
                    x: 0,
                    y: 0
                },
                parent
            );
        },
        createGroup(parent, data = undefined) {
            return Group.create(
                data || {
                    UUID: `group_${this.UUID()}`,
                    x: 0,
                    y: 0
                },
                parent
            );
        },
        // TODO: insert a <g> between Caddy and Shelf
        dragShelf(shelf) {
            const onStartDrag = d => {
                // for children, we set cache the current location of components
                // into xOrigin/yOrigin
                let gr = Component.getComponent(d.UUID);
                let components = gr.getComponents();
                let pos = this.getMousePos(d3.event.sourceEvent, this.svg);
                let baseShelf;
                components.forEach(comp => {
                    baseShelf = comp.data.baseShelf;
                    let posHookX = (comp.data.posHookX && comp.data.posHookX.length) > 0 ? comp.data.posHookX[0] : 0;
                    comp.data.xOrigin = baseShelf ? pos.x - comp.data.x : pos.x - comp.data.x - posHookX;
                    comp.data.yOrigin = pos.y - comp.data.y;
                });
            };
            const onUpdateDrag = (d, i, cmps) => {
                let event = d3.event;
                let cmp = cmps[0];

                d.x += event.dx;
                d.y += event.dy;
                d3.select(cmp).attr("transform", (d, i) => {
                    return "translate(" + [d.x, d.y] + ")";
                });
            };
            const onDragEnd = d => {
                let event = d3.event.sourceEvent;
                let pos = this.getMousePos(event, this.svg);
                let gr = Component.getComponent(d.UUID);
                // why sort the components?
                // EDIT: potential guess: SHELF is inside the getComponents list,
                // and we have to make it first.
                // EDIT2: it seems components are wrapped into virtual elements, here Group
                // And shelves/free pos are on their own groups
                let components = gr.getComponents().sort((c1, c2) => c2.type.localeCompare(c1.type));
                let shelf;
                components.forEach(comp => {
                    if (comp instanceof Shelf) {
                        shelf = this.makeSnapShelf(comp.data, pos, false);
                    } else if (comp.type === Component.TYPE_MODULE) {
                        // why free pos?
                        this.calculPositionModuleFreePos(comp.data, shelf);
                    }
                });

                gr.getElement().removeAttribute("transform");
                gr.data.x = 0;
                gr.data.y = 0;
            };

            shelf.drag(onStartDrag, onUpdateDrag, onDragEnd);
        },

        async createShelf(data, parent = undefined) {
            let UUIDShelf = "shelf_" + this.UUID();

            data.UUID = UUIDShelf;
            if (parent && this.isCaddySystem && !data.isCaddy) {
                parent = this.createGroup(parent);
            }
            if (!parent) {
                parent = this.createRootGroup(this.gContentComponentD3);
            }

            // We don't use Shelf.create, but these basically do the same
            let component = data.isCaddy ? new Caddy(data, parent) : new Shelf(data, parent);
            await component._init(data);
            component._initShelf();
            this.dragShelf(component);
            return component;
        },

        async makeSnapShelfFreePos(data, pos, isNew = true) {
            let shelf;
            let { x, y } = pos;
            if (isNew) {
                data.x = x;
                data.y = y;
                shelf = await this.createShelf(data);
            } else {
                shelf = Component.getComponent(data.UUID);
                let posInit = shelf.positionMouseStart;
                x = x - posInit.x;
                y = y - posInit.y;
                if (shelf) {
                    shelf.toPosition({ x, y }, false);
                }
            }

            return shelf;
        },

        async makeSnapShelf(data, pos, isNew = true) {
            if (this.isFreePosition) {
                return await this.makeSnapShelfFreePos(data, pos, isNew);
            }
            let { baseShelf } = data;
            let { x } = pos;
            let xOrigin = data.xOrigin ? parseInt(data.xOrigin, 10) : 0;
            let yOrigin = data.yOrigin ? parseInt(data.yOrigin, 10) : 0;
            x = x - xOrigin;
            let shelf = null;
            if (baseShelf) {
                let y = this.getInner.y + this.getInner.height - data.height;

                if (isNew) {
                    data.x = x;
                    data.y = y;
                    shelf = await this.createShelf(data);
                    if (!shelf.canSnap(data)) {
                        shelf.delete();
                        shelf = null;
                    }
                } else {
                    data.x = x;
                    data.y = y;

                    shelf = Component.getComponent(data.UUID);
                    if (shelf) {
                        console.warn("there");
                        shelf.toPosition({ x: x, y: y });
                    }
                }
            } else {
                const getShelfToPitchPosition = () => {
                    // TODO: check if the shelf is dropped within another shelf
                    let separators = this.getSeparators();
                    let widthSnap = data.posHookX[1] - data.posHookX[0];
                    const { posHookY } = data;
                    const posHY = posHookY[0] ? posHookY[0] : 0;
                    pos.y = pos.y + (posHY - yOrigin);
                    let xResult = 0;
                    let separatorsSnap = separators
                        .filter(sep => {
                            return sep.width === widthSnap;
                        })
                        .map((sep, i) => {
                            let sepa = this.assign(sep);
                            let xDiff = Math.abs(x - sepa.xStart);
                            sepa.xResult = xDiff;
                            if (i === 0) {
                                xResult = xDiff;
                            } else if (xDiff < xResult) {
                                xResult = xDiff;
                            }
                            return sepa;
                        })
                        .sort((sep1, sep2) => {
                            return sep1.xResult - sep2.xResult;
                        })
                        .filter(sep => sep.xResult === xResult);

                    let separatorSnap = null;
                    let resY = 0;
                    let resTmp = 1000;
                    separatorsSnap.forEach(sep => {
                        resY = Math.abs(sep.y - pos.y);
                        if (resY < resTmp) {
                            resTmp = resY;
                            // what is this dark magic?
                            // shouldn't we break the foreach if one item fit the description?
                            separatorSnap = sep;
                        }
                    });

                    if (separatorSnap) {
                        let hookXStart = data.posHookX[0];
                        let hookYStart = data.posHookY[0];
                        let x = separatorSnap.xStart - hookXStart;
                        let y = separatorSnap.y - hookYStart;

                        return {
                            x,
                            y
                        };
                    }
                };
                let destination, newParent;

                if (this.isCaddySystem) {
                    const [d, g] = plano.getShelf2CaddyDestination(Component.allComponents, pos, data);
                    destination = d;
                    newParent = g;
                    if (!data.isCaddy && destination) {
                        destination = {
                            ...destination,
                            x: destination.x - newParent.data.x,
                            y: destination.y - newParent.data.y
                        };
                    }
                }
                destination = destination || getShelfToPitchPosition();

                if (destination) {
                    data.x = destination.x;
                    data.y = destination.y;

                    if (isNew) {
                        shelf = await this.createShelf(data, newParent);
                        if (!this.isCaddySystem && !shelf.canSnap(data)) {
                            shelf.delete();
                            shelf = null;
                        }
                    } else {
                        shelf = Component.getComponent(data.UUID);

                        if (shelf) {
                            const caddy = findCaddyParent(shelf);
                            if (caddy && caddy !== newParent) {
                                shelf.changeParent(newParent);
                            }
                            shelf.toPosition(destination, !this.isCaddySystem);
                        }
                    }
                }
            }
            return shelf;
        },

        dragModule(module) {
            module.drag(
                d => {
                    let pos = this.getMousePos(d3.event.sourceEvent, this.svg);
                    d.xOrigin = pos.x - d.x;
                    d.originDragX = pos.x;
                    const component = Component.getComponent(d.UUID);
                    const caddy = component && findCaddyParent(component);

                    if (caddy) {
                        d.originLocalDragX = pos.x - caddy.data.x - d.x;
                    } else {
                        d.originLocalDragX = pos.x - d.x;
                    }
                },
                d => {
                    let event = d3.event;
                    const component = Component.getComponent(d.UUID);
                    component.draggable(event);
                },
                d => {
                    let event = d3.event.sourceEvent;
                    let pos = this.getMousePos(event, this.svg);
                    let elts = HTMLUtils.elementsFromPoint("#delete-components", event.clientX, event.clientY);
                    if (elts.length > 0) {
                        let component = Component.getComponent(d.UUID);
                        component.delete();
                    } else {
                        this.makeSnapModule(d, pos, false);
                    }
                }
            );
        },

        async createModule(data, parent) {
            let UUIDModule = "module_" + this.UUID();
            data.UUID = UUIDModule;
            data.xOrigin = 0;
            let module = await Module.create(data, parent);
            this.dragModule(module);
            return module;
        },

        async makeSnapModuleFreePosition(data, pos, isNew = true) {
            if (isNew) {
                let UUIDGroup = "group_" + this.UUID();
                let groupData = {
                    UUID: UUIDGroup,
                    x: 0,
                    y: 0
                };
                let group = Group.createRoot(groupData);
                data.x = pos.x;
                data.y = pos.y;
                data.freePosition = true;
                let module = await this.createModule(data, group);
                return module;
            } else {
                let module = Component.getComponent(data.UUID);
                module.data.freePosition = true;
                if (module.getGroup() !== module.getParent()) module.changeParent(module.getGroup());
                return module;
            }
        },

        calculPositionModuleFreePos(dataCmp, shelfPromise) {
            shelfPromise.then(shelf => {
                let module = Component.getComponent(dataCmp.UUID);
                let { distance } = shelf;
                let { data } = module;
                let x = data.x + distance.x;
                let y = data.y + distance.y;
                module.setPosition({ x, y });
            });
        },

        async makeSnapModule(data, pos, isNew = true) {
            if (this.isFreePosition) {
                return await this.makeSnapModuleFreePosition(data, pos, isNew);
            }

            data.freePosition = false;

            const compputeSnapDestinationY = (component, data) => {
                let posModY = component.data.posModY ? component.data.posModY : 0;
                let y;
                if (snapUp) {
                    y = component.data.y + posModY;
                    if (component.type === Component.TYPE_MODULE) {
                        y += component.data.height;
                    }
                } else {
                    y = component.data.y - data.height + posModY;
                }
                return y;
            };

            const computeSnapDestinationX = (x, dataMod, parent) => {
                if (this.isFreePosition) {
                    return x;
                }
                let widthMod = MathUtils.convertFloat(dataMod.width);
                let { UUID } = dataMod;
                x = MathUtils.convertFloat(x);
                let xRes = x;
                let res2, res, xMod, wMod;
                let children = parent
                    .getComponents()
                    .filter(cmp => cmp.data.UUID !== UUID)
                    .sort((cmp1, cmp2) => cmp1.data.x - cmp2.data.x);

                let xOrigin = dataMod.xOrigin ? parseInt(dataMod.xOrigin) : 0;
                children.forEach(cmp => {
                    xMod = cmp.data.x + xOrigin;
                    wMod = cmp.data.width;
                    res = xMod + wMod;
                    res2 = xRes + widthMod;
                    let condition = (xRes >= xMod && xRes <= res) || (res2 > xMod && res2 <= res);
                    if (condition) {
                        xRes = res + 1;
                    }
                });

                return xRes;
            };

            let globalDraggedComponentBounds = getGlobalBoundsForModuleAt(pos, data);
            let { snapUp } = data;

            let targets = Component.getAllComponents()
                // we cannot drop modules directly onto Caddy, we have to insert an inner shelf first
                .filter(c => !plano.isCaddy(c))
                // only components that can receive something
                .filter(cmp => cmp.type === Component.TYPE_SHELF || (cmp.type === Component.TYPE_MODULE && cmp.data.stackable))
                // cannot drop on self
                .filter(cmp => cmp.data.UUID !== data.UUID)
                // is a valid destination
                .filter(cmp => {
                    const destinationAlreadyOwnsDragged = !!cmp.findDescendantWithId(data.UUID);

                    return (
                        canSnapModuleHorizontally(globalDraggedComponentBounds, cmp, destinationAlreadyOwnsDragged) &&
                        canSnapModuleVertically(pos, cmp, data)
                    );
                })
                // Sort because we're droping on the first
                // this sorts targets based on their distance in the y axis with the pointer.
                .sort((c1, c2) => {
                    if (snapUp) {
                        return c2.data.y - c1.data.y;
                    } else {
                        let posModY1 = c1.data.posModY ? c1.data.posModY : 0;
                        let posModY2 = c2.data.posModY ? c2.data.posModY : 0;
                        return c1.data.y + posModY1 - (c2.data.y + posModY2);
                    }
                });

            const dropTargetComponent = targets[0];

            if (!dropTargetComponent) {
                if (!isNew) {
                    let component = Component.getComponent(data.UUID);
                    if (component) component.toPositionOrigin();
                    return component;
                }
                return;
            }

            const caddy = findCaddyParent(dropTargetComponent);

            let destinationX = pos.x;
            if (caddy) {
                destinationX -= caddy.data.x;
            }
            if (isNew) {
                data.y = compputeSnapDestinationY(dropTargetComponent, data);
                data.x = computeSnapDestinationX(destinationX, data, dropTargetComponent);
                data.xParent = destinationX - dropTargetComponent.data.x;
                const draggedCmp = await this.createModule(data, dropTargetComponent);
                if (!draggedCmp.validate()) {
                    draggedCmp.delete();
                }
                return draggedCmp;
            } else {
                const draggedCmp = Component.getComponent(data.UUID);
                destinationX = computeSnapDestinationX(destinationX, data, dropTargetComponent);

                data.y = compputeSnapDestinationY(dropTargetComponent, data);

                data.x = destinationX - data.originLocalDragX;

                const position = { x: data.x, y: data.y };
                const parentOld = draggedCmp.getParent();
                draggedCmp.changeParent(dropTargetComponent);
                if (draggedCmp.validate()) {
                    draggedCmp.toPosition(position);
                    if (parentOld.type === Component.TYPE_GROUP && parentOld.getComponents().length === 0) {
                        parentOld.delete();
                    }
                } else {
                    draggedCmp.changeParent(parentOld);
                    draggedCmp.toPositionOrigin();
                }
                data.xOrigin = 0;
                return draggedCmp;
            }
        },

        async createDivider(data) {
            let UUID = "divider_" + this.UUID();
            data.UUID = UUID;
            let divider = await Divider.create(data, this.gContentComponentD3);
            divider.drag(
                null,
                (d, i, cmps) => {
                    let event = d3.event;
                    let cmp = cmps[0];
                    d3.select(cmp)
                        .attr("x", data => {
                            data.x = event.x;
                            return data.x;
                        })
                        .attr("y", data => {
                            data.y = event.y;
                            return data.y;
                        });
                },
                (d, i, cmps) => {
                    let event = d3.event.sourceEvent;
                    let pos = this.getMousePos(event, this.svg);
                    this.makeSnapDivider(d, pos, false);
                }
            );
        },

        async makeSnapDivider(data, pos, isNew = true) {
            let dividersSlot = this.getDividersSlot;
            let distance = 1000000;
            let dividersSnap = dividersSlot
                .filter(div => {
                    return div.width === data.width && div.height >= data.height;
                })
                .map(div => {
                    let divNew = this.assign(div);
                    divNew.distance = Math.abs(div.x - pos.x);
                    if (distance > divNew.distance) {
                        distance = divNew.distance;
                    }
                    return divNew;
                })
                .filter(div => {
                    return div.distance === distance;
                });

            if (dividersSnap.length > 0) {
                let dividerSnap = dividersSnap[0];
                let y = pos.y;
                let res = y + data.height;
                let res2 = dividerSnap.y + dividerSnap.height;
                if (res > res2) {
                    let r = res - res2;
                    y = y - r;
                }

                let divid;
                if (isNew) {
                    divid = {
                        src: data.src,
                        width: data.width,
                        height: data.height,
                        x: dividerSnap.x,
                        y: y
                    };

                    this.createDivider(divid);
                } else {
                    let divider = Component.getComponent(data.UUID);
                    if (divider) {
                        divider.toPosition({ x: dividerSnap.x, y: y });
                    }
                }
            }
        }
    }
};
</script>

<style lang="scss">
.icon-size {
    font-size: 50px;
}

svg.caddy {
    // the important is necessary because when the CSS is minified, another rule takes priority
    overflow: visible !important;
}

svg {
    .bgWhite {
        background-color: white !important;
        fill: white !important;
    }
}

.content-template-svg {
    position: relative;
    width: 100%;
    height: 100%;

    .cluster-builder {
        padding-bottom: 100px;
    }
}

.content-menu,
.content-menu-hide {
    position: absolute;
    padding: 4px;
    z-index: 19;
    background: rgb(244, 244, 244);
    color: #000;
    cursor: pointer;
    font-size: 13px;
    font-family: "Roboto", Sans-Serif;
    border: 2px solid #fff;

    .layer-icon-hidden {
        visibility: hidden;
    }
}

.menu-item {
    padding: 5px 0px;
    background: rgb(244, 244, 244);
}

.menu-item:hover {
    background: rgb(200, 200, 200);
}
</style>



// WEBPACK FOOTER //
// TemplatePlano.vue?2bb9a04a